import React, { useEffect, useState } from "react";

import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Typography,
    Chip,
    Divider,
    Tabs,
    Tab
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import AxiosInstance from "../../services/AxiosInstance";

import OverviewTab from "./tabs/OverviewTab";
import TasksTab from "./tabs/TasksTab";
import ApprovalsTab from "./tabs/ApprovalsTab";
import AttachmentsTab from "./tabs/AttachmentsTab";
import CommentsTab from "./tabs/CommentsTab";
import ActivityTab from "./tabs/ActivityTab";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useAlert } from "../alerts/AlertContext";
import { CHANGE_REQUEST_URL } from "../../Constants/Constants";
import CreateChangeDialog from "./CreateChangeDialog";
import MoveChangeDialog
    from "./MoveChangeDialog";

import CancelChangeDialog
    from "./CancelChangeDialog";
import ChangeTabs from "./ChangeTabs";

const ChangeView = ({
    changeId,
    onBack
}) => {


    const WORKFLOW_STAGES = [
        {
            id: 1,
            code: "DRAFT",
            name: "Draft",
            nextCode: "ASSESSMENT",
            nextName: "Assessment"
        },
        {
            id: 2,
            code: "ASSESSMENT",
            name: "Assessment",
            nextCode: "APPROVAL_PENDING",
            nextName: "Approval Pending"
        },
        {
            id: 3,
            code: "APPROVAL_PENDING",
            name: "Approval Pending",
            nextCode: "SCHEDULED",
            nextName: "Scheduled"
        },
        {
            id: 4,
            code: "SCHEDULED",
            name: "Scheduled",
            nextCode: "IMPLEMENTATION",
            nextName: "Implementation"
        },
        {
            id: 5,
            code: "IMPLEMENTATION",
            name: "Implementation",
            nextCode: "TESTING",
            nextName: "Testing"
        },
        {
            id: 6,
            code: "TESTING",
            name: "Testing",
            nextCode: "COMPLETED",
            nextName: "Completed"
        },
        {
            id: 7,
            code: "COMPLETED",
            name: "Completed",
            nextCode: "CLOSED",
            nextName: "Closed"
        },
        {
            id: 8,
            code: "CLOSED",
            name: "Closed",
            nextCode: null,
            nextName: null
        }
    ];

    const [change, setChange] = useState(null);
    const showAlert = useAlert();
    const [loading, setLoading] =
        useState(true);

    const [tab, setTab] =
        useState(0);

    const [moveDialogOpen, setMoveDialogOpen] =
        useState(false);

    const [moving, setMoving] =
        useState(false);

    const [cancelDialogOpen, setCancelDialogOpen] =
        useState(false);

    const [editDialogOpen, setEditDialogOpen] =
        useState(false);

    const handleEdit = () => {

        setEditDialogOpen(true);

    };

    /*
     * Load change
     */
    const loadChange = async () => {

        setLoading(true);

        try {

            const response =
                await AxiosInstance.get(
                    `${CHANGE_REQUEST_URL}/${changeId}`
                );

            const data =
                response.data?.data ||
                response.data;

            setChange(data);

        } catch (error) {

            console.error(
                "Unable to load change",
                error
            );

        } finally {

            setLoading(false);
        }
    };


    const currentStage =
        WORKFLOW_STAGES.find(
            stage =>
                stage.code ===
                change?.statusCode
        );

    const nextStage =
        currentStage?.nextCode
            ? WORKFLOW_STAGES.find(
                stage =>
                    stage.code ===
                    currentStage.nextCode
            )
            : null;

    useEffect(() => {

        if (changeId) {
            loadChange();
        }

    }, [changeId]);



    /*
     * Loading
     */
    if (loading) {

        return (
            <Box
                sx={{
                    minHeight: 400,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                }}
            >
                <CircularProgress />
            </Box>
        );
    }


    /*
     * Not found
     */
    if (!change) {

        return (
            <Box sx={{ p: 3 }}>

                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                >
                    Back
                </Button>

                <Typography sx={{ mt: 3 }}>
                    Change not found.
                </Typography>

            </Box>
        );
    }


    return (

        <Box
            sx={{
                p: 2,
                width: "100%"
            }}
        >

            {/* =================================
                HEADER
            ================================= */}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3
                }}
            >
                {/* LEFT */}

                <Box>

                    <Button
                        startIcon={
                            <ArrowBackIcon />
                        }
                        onClick={onBack}
                    >
                        Back
                    </Button>

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <Typography
                            variant="h5"
                            fontWeight={600}
                        >
                            {change?.changeNumber || "Change Request"}
                        </Typography>

                        {/* Priority */}
                        <Chip
                            label={`Priority: ${change.priorityName || "-"}`}
                            size="small"
                            variant="outlined"
                        />

                        {/* Risk */}
                        <Chip
                            label={`Risk: ${change.riskName || "-"}`}
                            size="small"
                            variant="outlined"
                        />

                        {/* Stage */}
                        <Chip
                            label={`Stage: ${change.stageName || change.statusName || "-"}`}
                            size="small"
                            variant="outlined"
                        />
                    </Stack>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        {change?.title || "-"}
                    </Typography>

                </Box>


                {/* RIGHT */}

                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                >

                    {/* EDIT */}
                    {change?.statusCode === "DRAFT" && (
                        <Button
                            variant="outlined"
                            startIcon={
                                <EditOutlinedIcon />
                            }
                            onClick={handleEdit}
                            disabled={
                                moving ||
                                change?.statusCode === "CLOSED"
                            }
                        >
                            Edit
                        </Button>)}


                    {/* CANCEL */}

                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={
                            <CancelOutlinedIcon />
                        }
                        onClick={() =>
                            setCancelDialogOpen(true)
                        }
                        disabled={
                            moving ||
                            change?.statusCode === "CLOSED" ||
                            change?.statusCode === "COMPLETED"
                        }
                    >
                        Cancel Change
                    </Button>


                    {/* MOVE */}

                    {nextStage && (

                        <Button
                            variant="contained"
                            endIcon={
                                <ArrowForwardOutlinedIcon />
                            }
                            onClick={() =>
                                setMoveDialogOpen(true)
                            }
                            disabled={moving}
                        >
                            Move to {nextStage.name}
                        </Button>

                    )}

                </Stack>

                <CreateChangeDialog
                    open={editDialogOpen}
                    changeId={change?.id}
                    onClose={() =>
                        setEditDialogOpen(false)
                    }
                    onSaved={() => {

                        setEditDialogOpen(false);
                        loadChange();

                    }}
                />


                <MoveChangeDialog
                    open={moveDialogOpen}
                    change={change}
                    currentStage={currentStage}
                    nextStage={nextStage}
                    onClose={() =>
                        setMoveDialogOpen(false)
                    }
                    onSuccess={() => {

                        setMoveDialogOpen(false);
                        loadChange();

                    }}
                />

                <CancelChangeDialog
                    open={cancelDialogOpen}
                    change={change}
                    onClose={() =>
                        setCancelDialogOpen(false)
                    }
                    onSuccess={() => {

                        setCancelDialogOpen(false);
                        loadChange();

                    }}
                />

            </Box>


            {/* =================================
                WORKFLOW
            ================================= */}

            <WorkflowStages
                status={change.status}
                statusName={change.statusName}
            />


            {/* =================================
                TABS
            ================================= */}

            <ChangeTabs change={change}></ChangeTabs>
        </Box>
    );
};


/*
 * ============================================
 * WORKFLOW
 * ============================================
 */

const WorkflowStages = ({
    status,
    statusName
}) => {

    const stages = [
        "Draft",
        "Assessment",
        "Approval Pending",
        "Scheduled",
        "Implementation",
        "Testing",
        "Completed",
        "Closed"
    ];


    const currentStatus =
        String(
            statusName ||
            status ||
            "Draft"
        ).toLowerCase();


    const currentIndex =
        stages.findIndex(
            stage =>
                stage.toLowerCase() ===
                currentStatus
        );


    return (

        <Paper
            variant="outlined"
            sx={{
                p: 2,
                mb: 2
            }}
        >

            <Stack
                direction="row"
                alignItems="flex-start"
                spacing={0}
                sx={{
                    overflowX: "auto",
                    pb: 1
                }}
            >

                {stages.map(
                    (stage, index) => {

                        const completed =
                            currentIndex >= 0 &&
                            index < currentIndex;

                        const active =
                            index === currentIndex;


                        return (
                            <React.Fragment
                                key={stage}
                            >

                                <Box
                                    sx={{
                                        minWidth: 110,
                                        textAlign: "center"
                                    }}
                                >

                                    <Box
                                        sx={{
                                            width: 34,
                                            height: 34,
                                            borderRadius: "50%",
                                            mx: "auto",

                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",

                                            backgroundColor:
                                                active ||
                                                    completed
                                                    ? "primary.main"
                                                    : "action.disabledBackground",

                                            color:
                                                active ||
                                                    completed
                                                    ? "primary.contrastText"
                                                    : "text.secondary",

                                            fontWeight: 600
                                        }}
                                    >
                                        {index + 1}
                                    </Box>


                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: "block",
                                            mt: 1,
                                            fontWeight:
                                                active
                                                    ? 600
                                                    : 400
                                        }}
                                    >
                                        {stage}
                                    </Typography>

                                </Box>


                                {index <
                                    stages.length - 1 && (

                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 2,
                                                mt: 2.1,
                                                backgroundColor:
                                                    currentIndex > index
                                                        ? "primary.main"
                                                        : "divider"
                                            }}
                                        />

                                    )}

                            </React.Fragment>
                        );
                    }
                )}

            </Stack>

        </Paper>


    );
};


export default ChangeView;